import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminClientDetail from "@/pages/AdminClientDetail";
import NutritionPlanEditor from "@/pages/NutritionPlanEditor";
import StudioSettings from "@/pages/StudioSettings";
import ClientDashboard from "@/pages/ClientDashboard";
import { ClientPlansList, ClientPlanDetail } from "@/pages/ClientPlans";
import ClientMeasurements from "@/pages/ClientMeasurements";
import ClientPhotos from "@/pages/ClientPhotos";
import ClientPorzionario from "@/pages/ClientPorzionario";
import AppLayout from "@/components/AppLayout";
import { Toaster } from "sonner";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="label-tiny">Caricamento…</div>
    </div>
  );
}

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function AppRouter() {
  const location = useLocation();
  // AuthCallback: session_id arrives in URL hash. Detect during render (not useEffect) to avoid race.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Client area */}
      <Route path="/" element={<Protected><ClientHome /></Protected>} />
      <Route path="/piani" element={<Protected><ClientPlansList /></Protected>} />
      <Route path="/piani/:id" element={<Protected><ClientPlanDetail /></Protected>} />
      <Route path="/porzionario" element={<Protected><ClientPorzionario /></Protected>} />
      <Route path="/rilievi" element={<Protected><ClientMeasurements /></Protected>} />
      <Route path="/foto" element={<Protected><ClientPhotos /></Protected>} />

      {/* Admin area */}
      <Route path="/admin" element={<Protected adminOnly><AdminDashboard /></Protected>} />
      <Route path="/admin/clients/:id" element={<Protected adminOnly><AdminClientDetail /></Protected>} />
      <Route path="/admin/clients/:clientId/porzionario" element={<Protected adminOnly><ClientPorzionario /></Protected>} />
      <Route path="/admin/plans/:id" element={<Protected adminOnly><NutritionPlanEditor /></Protected>} />
      <Route path="/admin/studio" element={<Protected adminOnly><StudioSettings /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Client home: admins see admin dashboard, clients see their dashboard
function ClientHome() {
  const { user } = useAuth();
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  return <ClientDashboard />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
